/*
 * Purpose: Live map-build state — "watch AI build your flow" + Take control handoff
 * Last modified: 2026-07-10 | Agent
 * Completeness: 100
 */
import { create } from 'zustand';

export type MissionLiveBuildStatus = 'idle' | 'planning' | 'building' | 'done';

interface MissionLiveBuildState {
  status: MissionLiveBuildStatus;
  /** Outcome text that seeded the build (for banner + chat seed). */
  outcome: string;
  /** Total staged insertions (blocks) once planning resolves. */
  total: number;
  /** Blocks inserted so far during the staged animation. */
  inserted: number;
  /** True once the map assistant has echoed the outcome into chat. */
  chatSeeded: boolean;

  start: (outcome: string) => void;
  setPlanning: () => void;
  setBuilding: (total: number) => void;
  setInserted: (count: number) => void;
  finish: () => void;
  markChatSeeded: () => void;
  reset: () => void;
}

export const useMissionLiveBuildStore = create<MissionLiveBuildState>((set) => ({
  status: 'idle',
  outcome: '',
  total: 0,
  inserted: 0,
  chatSeeded: false,

  start: (outcome) =>
    set({ status: 'planning', outcome: outcome.trim(), total: 0, inserted: 0, chatSeeded: false }),
  setPlanning: () => set({ status: 'planning' }),
  setBuilding: (total) => set({ status: 'building', total, inserted: 0 }),
  setInserted: (count) => set({ inserted: count }),
  finish: () => set((s) => ({ status: 'done', inserted: s.total || s.inserted })),
  markChatSeeded: () => set({ chatSeeded: true }),
  reset: () => set({ status: 'idle', outcome: '', total: 0, inserted: 0, chatSeeded: false }),
}));

/** True while the AI is planning or animating the map build. */
export function isLiveBuildActive(status: MissionLiveBuildStatus): boolean {
  return status === 'planning' || status === 'building';
}
